import React, { useEffect, useState } from "react";
import {
  PlugZap, RefreshCw, RotateCcw, ListChecks, Eye, CheckCircle2, XCircle,
  Cloud, AlertTriangle, KeyRound, Save,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel, StatusBadge } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const SYNC_STATUS = {
  RUNNING: "bg-blue-50 text-blue-600 border-blue-200",
  SUCCESS: "bg-green-50 text-green-700 border-green-200",
  PARTIAL_SUCCESS: "bg-orange-50 text-orange-600 border-orange-200",
  FAILED: "bg-red-50 text-red-600 border-red-200",
};

function LogBadge({ status }) {
  return <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold border ${SYNC_STATUS[status] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{status}</span>;
}

export default function PosSync() {
  const { can } = useAuth();
  const isAdmin = can("warehouse_admin");
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = () => {
    api.get("/pos/config").then((r) => setConfig(r.data)).catch(() => {});
    api.get("/pos/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/pos/sync-logs").then((r) => setLogs(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    const t = setInterval(() => { api.get("/pos/status").then((r) => setStatus(r.data)).catch(() => {}); }, 5000);
    return () => clearInterval(t);
  }, []);

  const saveKey = async () => {
    if (!apiKey.trim()) return toast.error("Masukkan API key");
    setBusy(true);
    try { await api.put("/pos/config", { api_key: apiKey.trim() }); toast.success("API key tersimpan (backend)"); setApiKey(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const testConn = async () => {
    setBusy(true);
    try { await api.post("/pos/test-connection"); toast.success("Koneksi POS berhasil"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
    finally { setBusy(false); }
  };

  const runSync = async () => {
    setConfirmOpen(false); setBusy(true);
    try { const r = await api.post("/pos/sync"); toast.success(r.data.message); setTimeout(load, 1500); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const retryFailed = async () => {
    setBusy(true);
    try { const r = await api.post("/pos/retry-failed"); toast.success(r.data.message); setTimeout(load, 1500); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openDetail = (id) => api.get(`/pos/sync-logs/${id}`).then((r) => setDetail(r.data));

  const counts = status?.counts || {};
  const last = status?.last_sync;

  return (
    <div data-testid="pos-sync-page">
      <PageHeader title="Sinkronisasi POS" subtitle="Master Data → Ambil master produk dari POS Ventedaily (source of truth)">
        {isAdmin && (
          <>
            <Button variant="outline" disabled={busy} onClick={testConn} data-testid="pos-test-btn" className="border-taupe text-cocoa"><PlugZap className="w-4 h-4 mr-1.5" /> Tes Koneksi</Button>
            <Button variant="outline" disabled={busy} onClick={retryFailed} data-testid="pos-retry-btn" className="border-taupe text-cocoa"><RotateCcw className="w-4 h-4 mr-1.5" /> Coba Ulang Gagal</Button>
            <Button disabled={busy || status?.running} onClick={() => setConfirmOpen(true)} data-testid="pos-sync-all-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory"><RefreshCw className={`w-4 h-4 mr-1.5 ${status?.running ? "animate-spin" : ""}`} /> {status?.running ? "Sedang Sinkron..." : "Sinkron Semua Produk"}</Button>
          </>
        )}
      </PageHeader>

      {/* Connection & config */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Panel className="p-5 lg:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config?.configured ? "bg-green-50" : "bg-slate-100"}`}>
              <Cloud className={`w-5 h-5 ${config?.configured ? "text-status-passed" : "text-wmuted"}`} />
            </div>
            <div>
              <div className="font-heading font-bold text-cocoa">POS Ventedaily</div>
              <div className="text-xs text-wmuted font-mono">{config?.base_url}</div>
            </div>
            <div className="ml-auto text-right">
              <div className="text-xs text-wmuted">Status Koneksi</div>
              <div className={`font-semibold ${status?.connected ? "text-status-passed" : status?.connected === false ? "text-status-reject" : "text-wmuted"}`}>
                {status?.connected ? "Terhubung" : status?.connected === false ? "Gagal" : "Belum dites"}
              </div>
            </div>
          </div>
          {isAdmin && (
            <div className="border-t border-taupe/15 pt-4">
              <Label className="flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> API Key POS {config?.configured && <span className="text-xs text-status-passed">(tersimpan: {config.api_key_masked})</span>}</Label>
              <div className="flex gap-2 mt-1.5">
                <Input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="Tempel X-API-Key di sini (disimpan hanya di backend)" data-testid="pos-apikey-input" />
                <Button onClick={saveKey} disabled={busy} data-testid="pos-save-key-btn" className="bg-taupe hover:bg-taupe/90 text-white whitespace-nowrap"><Save className="w-4 h-4 mr-1.5" /> Simpan</Button>
              </div>
              <p className="text-xs text-wmuted mt-2">API key tidak pernah dikirim ke frontend/log dan tidak disimpan di browser. Interval sinkronisasi otomatis: {config?.interval_minutes} menit.</p>
            </div>
          )}
        </Panel>

        <Panel className="p-5">
          <div className="text-xs text-wmuted uppercase font-semibold tracking-wide">Sinkronisasi Terakhir</div>
          {last ? (
            <>
              <div className="mt-2"><LogBadge status={last.status} /></div>
              <div className="text-sm text-cocoa mt-2">{fmtDate(last.started_at)}</div>
              <div className="text-xs text-wmuted">{last.trigger_type} · oleh {last.initiated_by}</div>
              {last.error_summary && <div className="text-xs text-status-reject mt-2 flex items-start gap-1"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{last.error_summary}</div>}
            </>
          ) : <div className="text-sm text-wmuted mt-3">Belum pernah sinkron.</div>}
        </Panel>
      </div>

      {/* Count cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <CountCard label="Produk POS (WMS)" value={counts.total_products} />
        <CountCard label="Tersinkron" value={counts.synced_products} tone="text-status-passed" />
        <CountCard label="Varian Tersinkron" value={counts.total_variants} />
        <CountCard label="Data Baru" value={last?.total_created} />
        <CountCard label="Diperbarui" value={last?.total_updated} />
        <CountCard label="Gagal" value={counts.failed_products} tone="text-status-reject" />
      </div>

      {/* Logs */}
      <Panel>
        <div className="p-4 border-b border-taupe/15 font-heading font-bold text-cocoa">Riwayat Sinkronisasi</div>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Waktu</TableHead><TableHead>Trigger</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right">Diterima</TableHead><TableHead className="text-right">Baru</TableHead>
              <TableHead className="text-right">Update</TableHead><TableHead className="text-right">Nonaktif</TableHead>
              <TableHead className="text-right">Gagal</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.map((l) => (
              <TableRow key={l.id} data-testid={`sync-log-${l.id}`}>
                <TableCell className="text-xs whitespace-nowrap">{fmtDate(l.started_at)}</TableCell>
                <TableCell className="text-xs">{l.trigger_type}</TableCell>
                <TableCell><LogBadge status={l.status} /></TableCell>
                <TableCell className="text-right">{l.total_received}</TableCell>
                <TableCell className="text-right text-status-passed font-semibold">{l.total_created}</TableCell>
                <TableCell className="text-right">{l.total_updated}</TableCell>
                <TableCell className="text-right text-wmuted">{l.total_deactivated}</TableCell>
                <TableCell className="text-right text-status-reject font-semibold">{l.total_failed}</TableCell>
                <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => openDetail(l.id)} data-testid={`view-log-${l.id}`}><Eye className="w-4 h-4" /></Button></TableCell>
              </TableRow>
            ))}
            {!logs.length && <TableRow><TableCell colSpan={9} className="text-center text-wmuted py-10">Belum ada riwayat sinkronisasi.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Panel>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-cocoa">Sinkron Semua Produk?</AlertDialogTitle>
            <AlertDialogDescription>
              Proses ini <b>hanya memperbarui master produk</b> (nama, varian, warna, ukuran, harga POS sebagai referensi) dari POS Ventedaily.
              <br /><br />Proses ini <b>TIDAK mengubah stok fisik WMS</b>, hasil QC, penerimaan barang, atau riwayat transaksi.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={runSync} data-testid="confirm-sync-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory">Ya, Sinkron Sekarang</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {detail && (
        <Dialog open onOpenChange={() => setDetail(null)}>
          <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading text-cocoa flex items-center gap-3">Detail Log <LogBadge status={detail.log.status} /></DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
              <Info label="Diterima" v={detail.log.total_received} />
              <Info label="Baru" v={detail.log.total_created} />
              <Info label="Diperbarui" v={detail.log.total_updated} />
              <Info label="Tidak Berubah" v={detail.log.total_unchanged} />
              <Info label="Nonaktif" v={detail.log.total_deactivated} />
              <Info label="Gagal" v={detail.log.total_failed} />
            </div>
            {detail.log.breakdown && (
              <div className="text-xs text-wmuted mb-3">Kategori: {detail.log.breakdown.categories} · Warna: {detail.log.breakdown.colors} · Ukuran: {detail.log.breakdown.sizes} · Produk: {detail.log.breakdown.products} · Varian: {detail.log.breakdown.variants}</div>
            )}
            <div className="font-semibold text-cocoa text-sm mb-2">Detail Item ({detail.items.length})</div>
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Entitas</TableHead><TableHead>Ref POS</TableHead><TableHead>Aksi</TableHead><TableHead>Status</TableHead><TableHead>Pesan</TableHead></TableRow></TableHeader>
              <TableBody>
                {detail.items.map((it) => (
                  <TableRow key={it.id}>
                    <TableCell className="text-xs">{it.entity_type}</TableCell>
                    <TableCell className="font-mono text-xs">{it.pos_reference_id || "-"}</TableCell>
                    <TableCell className="text-xs">{it.action}</TableCell>
                    <TableCell>{it.status === "SUCCESS" ? <CheckCircle2 className="w-4 h-4 text-status-passed" /> : <XCircle className="w-4 h-4 text-status-reject" />}</TableCell>
                    <TableCell className="text-xs text-wmuted">{it.error_message || "-"}</TableCell>
                  </TableRow>
                ))}
                {!detail.items.length && <TableRow><TableCell colSpan={5} className="text-center text-wmuted py-6">Tidak ada item error/nonaktif.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

const CountCard = ({ label, value, tone }) => (
  <Panel className="p-4">
    <div className="text-xs font-semibold text-wmuted uppercase tracking-wide">{label}</div>
    <div className={`mt-2 text-2xl font-heading font-bold ${tone || "text-cocoa"}`}>{value ?? 0}</div>
  </Panel>
);
const Info = ({ label, v }) => (<div><div className="text-xs text-wmuted">{label}</div><div className="font-semibold text-cocoa">{v ?? 0}</div></div>);
